import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// core
import {
  Typography,
  InputLabel,
  TextField,
  Box,
} from '@material-ui/core';

// Components
import Button from '../../components/Button';
import Snackbar from '../../components/Snackbar';
import OutlinedSelect from '../../components/OutlinedSelect';
import ImageUpload from '../../components/ImageUpload';
import countries from '../Register/countries';

import { postNewLocation, getLocation, putEditLocation } from '../../requests/api/location';
import { getCompanies } from '../../requests/api/company';
import useStyles from './styles';

const LocationForm = ({ locationId }) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const currentCompany = useSelector((state) => state.company.companyId);
  const [inputValues, setInputValues] = useState({
    companyId: currentCompany,
    name: '',
    internalCode: '',
    chain: '',
    chainSubCategory: '',
    deleted: 'false',
    imageUrl: '',
    countryCode: '',
    city: '',
    region: '',
    sector: '',
    zone: '',
    address: '',
    latitude: '',
    longitude: '',
    portraitImageUrl: '',
    cluster: '',
  });

  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const deletedOptions = [
    {
      name: t('locations.locationForm.deletedOption'),
      value: 'true',
    },
    {
      name: t('locations.locationForm.activeOption'),
      value: 'false',
    },
  ];

  useEffect(() => {
    const getCompaniesRequest = async () => {
      const response = await getCompanies();
      if (response.success) {
        const allCompanies = response.data.data.companies;
        const companiesOptions = allCompanies.map(
          (c) => ({ name: c.name, value: c.id }),
        ).filter((c) => c.value === currentCompany);
        setCompanies(companiesOptions);
      }
    };

    getCompaniesRequest();
    // eslint-disable-next-line
  }, []);

  const selectCountry = (e) => {
    const { value } = e.target;
    setInputValues({
      ...inputValues,
      countryCode: value,
    });
  };

  useEffect(() => {
    const getLocationRequest = async () => {
      const response = await getLocation(locationId);
      if (response.success) {
        const { location } = response.data.data;
        setInputValues({
          companyId: location.companyId,
          name: location.name,
          internalCode: location.internalCode,
          chain: location.chain,
          chainSubCategory: location.chainSubCategory,
          deleted: location.deleted != null ? `${location.deleted}` : 'false',
          imageUrl: location.imageUrl || '',
          countryCode: location.Address.countryCode,
          city: location.Address.city,
          region: location.Address.region,
          sector: location.Address.sector,
          zone: location.Address.zone,
          address: location.Address.address,
          latitude: location.Address.latitude,
          longitude: location.Address.longitude,
          portraitImageUrl: location.portraitImageUrl || '',
          cluster: location.cluster || '',
        });
      }
    };
    if (locationId) {
      getLocationRequest();
    }
  }, [locationId]);

  const handleSave = async () => {
    if (saving) {
      return null;
    }

    try {
      if (inputValues.name === '') {
        setSeverity('error');
        setMessage(t('locations.locationForm.nameMissingError'));
        return null;
      }
      if (inputValues.countryCode === '') {
        setSeverity('error');
        setMessage(t('locations.locationForm.countryCodeMissingError'));
        return null;
      }
      if (inputValues.city === '') {
        setSeverity('error');
        setMessage(t('locations.locationForm.cityMissingError'));
        return null;
      }
      if (inputValues.region === '') {
        setSeverity('error');
        setMessage(t('locations.locationForm.regionMissingError'));
        return null;
      }
      if (inputValues.address === '') {
        setSeverity('error');
        setMessage(t('locations.locationForm.addressMissingError'));
        return null;
      }

      setSaving(true);

      let response = null;
      if (locationId) {
        response = await putEditLocation(locationId, inputValues);
      } else {
        response = await postNewLocation(inputValues);
      }

      if (response.status === 200) {
        setSeverity('success');
        setMessage(t('common.savedSuccessfully'));
        window.location.reload();
      } else {
        setSeverity('error');
        setMessage(response.data.errorMessage);
        setSaving(false);
      }
    } catch (err) {
      setSeverity('error');
      setMessage('error');
      setSaving(false);
      return (err);
    }
    return (false);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setInputValues({
      ...inputValues,
      [id]: value,
    });
  };

  const selectCompany = (e) => {
    const { value } = e.target;
    setInputValues({
      ...inputValues,
      companyId: value,
    });
  };

  const handleChangePortraitImage = (e) => {
    if (e === 'Error') {
      setSeverity('error');
      setMessage(t('common.fileAddedUnsuccessfully'));
      return;
    }
    setInputValues({ ...inputValues, portraitImageUrl: e });
  };

  const handleChangeImage = (e) => {
    if (e === 'Error') {
      setSeverity('error');
      setMessage(t('common.fileAddedUnsuccessfully'));
      return;
    }
    setInputValues({ ...inputValues, imageUrl: e });
  };

  return (
    <div className={styles.formWrapper}>
      <div className={styles.locationInfo}>
        <div>
          <Typography className={styles.addressTitle}>{t('locations.locationForm.image')}</Typography>
        </div>
        <div>
          <Typography className={styles.addressTitle}>{t('locations.locationForm.portraitImage')}</Typography>
        </div>
        <div>
          <ImageUpload
            value={inputValues.imageUrl}
            onChange={handleChangeImage}
            label={t('locations.locationForm.image')}
            hint={t('locations.locationForm.imageHint')}
            width={200}
            height={200}
            preserveAspectRatio
          />
        </div>
        <div>
          <ImageUpload
            value={inputValues.portraitImageUrl}
            onChange={handleChangePortraitImage}
            label={t('locations.locationForm.portraitImage')}
            resize={false}
          />
        </div>
        <div>
          <Typography className={styles.addressTitle}>{t('locations.locationForm.locationTitle')}</Typography>
        </div>
        <div />
        <div>
          <Typography display="inline">{t('locations.locationForm.name')}</Typography>
          <Typography className={styles.required} display="inline">{'   *'}</Typography>
          <TextField
            id="name"
            type="text"
            variant="outlined"
            value={inputValues.name}
            onChange={handleChange}
            fullWidth
          />
        </div>
        <div>
          <Typography display="inline">{t('locations.locationForm.internalCode')}</Typography>
          <TextField
            id="internalCode"
            type="text"
            value={inputValues.internalCode}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography display="inline">{t('locations.locationForm.company')}</Typography>
          <Typography className={styles.required} display="inline">{'   *'}</Typography>
          <OutlinedSelect
            id="company"
            name="company"
            items={companies}
            value={inputValues.companyId}
            onChange={selectCompany}
            fullWidth
            disabled
          />
        </div>
        <div>
          <Typography>{t('locations.locationForm.chain')}</Typography>
          <TextField
            id='chain'
            type="text"
            variant='outlined'
            fullWidth
            value={inputValues.chain}
            onChange={handleChange}
          />
        </div>
        <div>
          <Typography>{t('locations.locationForm.chainSubCategory')}</Typography>
          <TextField
            id="chainSubCategory"
            type="text"
            value={inputValues.chainSubCategory}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography>Cluster</Typography>
          <TextField
            id="cluster"
            type="text"
            value={inputValues.cluster}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography className={styles.addressTitle}>{t('locations.locationForm.addressTitle')}</Typography>
        </div>
        <div />
        <div>
          <Typography display="inline">{t('locations.locationForm.countryCode')}</Typography>
          <Typography className={styles.required} display="inline">{'   *'}</Typography>
          <OutlinedSelect
            id="countryCode"
            type="text"
            variant="outlined"
            value={inputValues.countryCode}
            onChange={selectCountry}
            select={true}
            items={countries}
            fullWidth
          />
        </div>
        <div>
          <Typography display="inline">{t('locations.locationForm.city')}</Typography>
          <Typography className={styles.required} display="inline">{'   *'}</Typography>
          <TextField
            id="city"
            type="text"
            value={inputValues.city}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography display="inline">{t('locations.locationForm.region')}</Typography>
          <Typography className={styles.required} display="inline">{'   *'}</Typography>
          <TextField
            id="region"
            type="text"
            value={inputValues.region}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <InputLabel>{t('locations.locationForm.sector')}</InputLabel>
          <TextField
            id="sector"
            type="text"
            value={inputValues.sector}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography>{t('locations.locationForm.zone')}</Typography>
          <TextField
            id="zone"
            type="text"
            value={inputValues.zone}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography display="inline">{t('locations.locationForm.address')}</Typography>
          <Typography className={styles.required} display="inline">{'   *'}</Typography>
          <TextField
            id="address"
            type="text"
            value={inputValues.address}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography>{t('locations.locationForm.latitude')}</Typography>
          <TextField
            id="latitude"
            type="number"
            value={inputValues.latitude}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
        <div>
          <Typography>{t('locations.locationForm.longitude')}</Typography>
          <TextField
            id="longitude"
            type="number"
            value={inputValues.longitude}
            onChange={handleChange}
            variant="outlined"
            fullWidth
          />
        </div>
      </div>
      <Box className={styles.buttonsContainerForm} >
        <Button
          id="submit-btn"
          onClick={handleSave}
          text={t('common.save')}
          disabled={saving}
          height="35px"
          width="100%"
          backgroundColor="#2C9DC9"
          color="#FFFFFF"
          borderRadius="10px"
        />
      </Box>
      <div>
        <Snackbar
          open={message !== ''}
          message={message}
          severity={severity}
          onClose={() => setMessage('')}
        />
      </div>
    </div >
  );
};

export default LocationForm;
